import { IComboBoxOption } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { ComboWithFilter } from 'components/Utils/comboWithFilter';
import { CopilotQuestionOption } from 'models/copilot/copilotQuestionOption';
import { AutomationQuestion } from 'models/copilot/questionSet';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCopilotGetQuestionOptions } from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';

// a source that answers fewer rows than this is loaded once and filtered on the typed text in the browser
const loadOnceMax = 200;
// the rows one search returns when the question names no max
const defaultSearchMax = 50;
const searchDelayMs = 300;

interface IQuestionLookupProps {
  question: AutomationQuestion;
  selected: CopilotQuestionOption[];
  onChange: (selected: CopilotQuestionOption[]) => void;
}

/**
 * A lookup question of the question card: the tenant's own rows of one source (templates, tags, users, risks, ...),
 * picked in a type-to-filter combobox, several when the question is multi. A small source is loaded once; a large one
 * is searched on the typed text. The chosen rows keep their ids verbatim for the answer.
 */
export const QuestionLookup = (props: IQuestionLookupProps) => {
  const { t } = useTranslation(['copilot']);
  const appContext = useContext(AppContext);
  const [options, setOptions] = useState<CopilotQuestionOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // the first load returned the whole source: the combobox filters it, no search requests
  const [isLoadedOnce, setIsLoadedOnce] = useState<boolean>(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // the number of the latest request, so a slower earlier answer does not replace a newer one
  const requestRef = useRef<number>(0);

  const { source, filter, max } = props.question;
  const filterKey = JSON.stringify(filter ?? {});

  const load = async (search: string | undefined, rowMax: number): Promise<CopilotQuestionOption[] | undefined> => {
    if (!source) return undefined;

    const request = ++requestRef.current;
    setIsLoading(true);
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const rows = await apiCopilotGetQuestionOptions(source, filter, search, rowMax, accessToken);
      if (request !== requestRef.current) return undefined;

      setOptions(rows);

      return rows;
    } catch (err) {
      // a refused source or filter is a defect of the card, a refused read the user's permission: both are shown
      appContext.setError(err);

      return undefined;
    } finally {
      if (request === requestRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    let isCurrent = true;
    load(undefined, loadOnceMax).then((rows) => {
      if (isCurrent && rows) setIsLoadedOnce(rows.length < loadOnceMax);
    });

    return () => {
      isCurrent = false;
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
    // the question is fixed for the card's life; the filter is compared by value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, filterKey]);

  const onSearch = (text: string | undefined) => {
    if (isLoadedOnce) return;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    searchTimerRef.current = setTimeout(() => {
      void load(text?.trim() || undefined, max ?? defaultSearchMax);
    }, searchDelayMs);
  };

  // the chosen rows stay in the list, so their tags survive a search that no longer returns them
  const knownOptions = useMemo((): CopilotQuestionOption[] => {
    const byId = new Map<string, CopilotQuestionOption>();
    for (const option of [...props.selected, ...options]) byId.set(option.id, option);

    return Array.from(byId.values());
  }, [options, props.selected]);

  const comboOptions = useMemo(
    (): IComboBoxOption[] =>
      knownOptions.map((option) => ({ key: option.id, text: option.label, title: option.description })),
    [knownOptions],
  );

  const selectedIds = props.selected.map((option) => option.id);

  const onSelectedKeysChange = (keys: string[]) => {
    // one row for a single question: the key just picked replaces the previous one
    const added = keys.filter((key) => !selectedIds.includes(key));
    const ids = props.question.multi === true || added.length === 0 ? keys : [added[added.length - 1]];

    props.onChange(
      ids
        .map((id) => knownOptions.find((option) => option.id === id))
        .filter((option): option is CopilotQuestionOption => option !== undefined),
    );
  };

  return (
    <ComboWithFilter
      label={props.question.text}
      required={props.question.required === true}
      options={comboOptions}
      selectedKeys={selectedIds}
      onSelectedKeysChange={onSelectedKeysChange}
      onInputValueChange={onSearch}
      placeholder={
        isLoading ? t('copilot:Chat.Questions.LookupLoading') : t('copilot:Chat.Questions.LookupPlaceholder')
      }
      noMatchText={isLoading ? undefined : t('copilot:Chat.Questions.LookupNoMatch')}
    />
  );
};

export default QuestionLookup;
